/**
 * WB4 Depth — Role navigator queue display + count merge helpers.
 * Domain catalog + role filter — "다른 세계" navigator lens per cockpit role.
 */

import type {
  RoleNavigatorConfig,
  RoleNavigatorQueueConfig,
  WorkbenchRole,
} from './workbench-role-cockpit-config';
import type {
  RoleCompositionNavigatorPanelMode,
  RoleCompositionSpec,
} from './workbench-role-composition-registry';

export type RoleNavigatorQueueCountStatus = 'ok' | 'loading' | 'unknown' | 'stale' | 'error';

export type RoleNavigatorQueueCountProvenance = 'server' | 'cache' | 'fallback' | 'none';

export interface RoleNavigatorQueueBadge {
  count?: number;
  severity?: 'info' | 'warning' | 'danger';
  status?: RoleNavigatorQueueCountStatus;
  source?: RoleNavigatorQueueCountProvenance;
  updatedAt?: string;
}

export interface RoleNavigatorQueueDisplay {
  id: string;
  label: string;
  description?: string;
  domain?: string;
  filter?: Record<string, unknown>;
  defaultAction?: string;
  badge?: RoleNavigatorQueueBadge;
}

export interface RoleNavigatorQueueCountEntry {
  countSource: string;
  count?: number;
  status?: RoleNavigatorQueueCountStatus;
  source?: RoleNavigatorQueueCountProvenance;
  updatedAt?: string;
}

/** Logical business domain → unified runtime route entry (grammar-proxy aligned). */
export interface WorkbenchNavigatorDomainEntry {
  id: string;
  domain: string;
  subdomain: string;
  label: string;
}

export const WORKBENCH_NAVIGATOR_DOMAIN_CATALOG: Readonly<
  Record<string, WorkbenchNavigatorDomainEntry>
> = {
  customer: {
    id: 'customer',
    domain: 'entity',
    subdomain: 'external',
    label: '고객',
  },
  order: {
    id: 'order',
    domain: 'manufacturing',
    subdomain: 'production-order',
    label: '주문',
  },
  inventory: {
    id: 'inventory',
    domain: 'inventory',
    subdomain: 'shortage',
    label: '재고',
  },
  finance: {
    id: 'finance',
    domain: 'finance',
    subdomain: 'receivables',
    label: '채권·수금',
  },
  entity: {
    id: 'entity',
    domain: 'entity',
    subdomain: 'internal',
    label: '조직·엔티티',
  },
  item: {
    id: 'item',
    domain: 'item',
    subdomain: 'sellable',
    label: '판매 가능 항목',
  },
};

/**
 * Role cockpit "world" — which navigator domains each role may see.
 * Intersected with queue/defaultLanding domains from `RoleNavigatorConfig`.
 */
export const ROLE_NAVIGATOR_DOMAIN_FILTER: Readonly<
  Partial<Record<WorkbenchRole, readonly string[]>>
> = {
  sales: ['customer', 'order'],
  finance: ['finance'],
  warehouse: ['inventory', 'order'],
  admin: ['entity'],
  owner: ['customer', 'finance'],
  operations: ['inventory', 'order'],
};

const ROLE_NAVIGATOR_DOMAIN_CATALOG_ORDER: readonly string[] = [
  'customer',
  'order',
  'inventory',
  'finance',
  'entity',
  'item',
];

function sortRoleNavigatorDomains(
  entries: WorkbenchNavigatorDomainEntry[],
  defaultDomainId: string,
  navigatorPanelMode: RoleCompositionNavigatorPanelMode | undefined,
  queueDomainIds: ReadonlySet<string>
): WorkbenchNavigatorDomainEntry[] {
  const catalogRank = new Map(ROLE_NAVIGATOR_DOMAIN_CATALOG_ORDER.map((id, index) => [id, index]));

  return [...entries].sort((left, right) => {
    if (left.id === defaultDomainId) {
      return -1;
    }
    if (right.id === defaultDomainId) {
      return 1;
    }

    if (navigatorPanelMode === 'queue_first') {
      const leftInQueue = queueDomainIds.has(left.id);
      const rightInQueue = queueDomainIds.has(right.id);
      if (leftInQueue !== rightInQueue) {
        return leftInQueue ? -1 : 1;
      }
    }

    const leftRank = catalogRank.get(left.id) ?? ROLE_NAVIGATOR_DOMAIN_CATALOG_ORDER.length;
    const rightRank = catalogRank.get(right.id) ?? ROLE_NAVIGATOR_DOMAIN_CATALOG_ORDER.length;
    return leftRank - rightRank;
  });
}

export function resolveRoleNavigatorDomains(
  role: WorkbenchRole,
  navigatorConfig: Pick<RoleNavigatorConfig, 'defaultLanding' | 'queues'>,
  composition?: Pick<RoleCompositionSpec, 'navigatorPanelMode'>
): WorkbenchNavigatorDomainEntry[] {
  const configDomainIds = new Set<string>([
    navigatorConfig.defaultLanding.domain,
    ...navigatorConfig.queues.map((queue) => queue.domain),
  ]);
  const queueDomainIds = new Set(navigatorConfig.queues.map((queue) => queue.domain));
  const roleAllowlist = ROLE_NAVIGATOR_DOMAIN_FILTER[role];

  const candidateIds =
    roleAllowlist !== undefined
      ? roleAllowlist.filter(
          (domainId) => WORKBENCH_NAVIGATOR_DOMAIN_CATALOG[domainId] !== undefined
        )
      : [...configDomainIds].filter(
          (domainId) => WORKBENCH_NAVIGATOR_DOMAIN_CATALOG[domainId] !== undefined
        );

  const domainIds = new Set(candidateIds);
  if (WORKBENCH_NAVIGATOR_DOMAIN_CATALOG[navigatorConfig.defaultLanding.domain] !== undefined) {
    domainIds.add(navigatorConfig.defaultLanding.domain);
  }

  const entries = [...domainIds]
    .map((domainId) => WORKBENCH_NAVIGATOR_DOMAIN_CATALOG[domainId])
    .filter((entry): entry is WorkbenchNavigatorDomainEntry => entry !== undefined);

  return sortRoleNavigatorDomains(
    entries,
    navigatorConfig.defaultLanding.domain,
    composition?.navigatorPanelMode,
    queueDomainIds
  );
}

/** Pilot stub counts — sales-ar-aging · warehouse-picking first. */
export const ROLE_QUEUE_COUNT_DISPLAY_STALE_TTL_MS = 5 * 60 * 1000;
export const PILOT_ROLE_QUEUE_COUNT_STUBS: Readonly<
  Record<WorkbenchRole, Readonly<Partial<Record<string, number>>>>
> = {
  sales: {
    'ar-aging': 12,
    'credit-warning': 3,
  },
  warehouse: {
    'picking-pending': 5,
    'low-stock': 2,
  },
  finance: {
    'overdue-ar': 4,
    'correction-approver-pending': 2,
  },
  admin: {
    'permission-issues': 1,
    'correction-approver-pending': 2,
  },
  owner: {
    'credit-hold': 2,
  },
  operations: {
    'picking-pending': 5,
    'low-stock': 2,
  },
};

export function resolvePilotRoleQueueCount(
  role: WorkbenchRole,
  countSource: string,
  tenantId: string
): number {
  const base = PILOT_ROLE_QUEUE_COUNT_STUBS[role]?.[countSource] ?? 0;
  if (base === 0) {
    return 0;
  }
  const tenantSeed = tenantId.trim().length % 3;
  return base + tenantSeed;
}

export function roleNavigatorQueueCountsToMap(
  entries: readonly RoleNavigatorQueueCountEntry[]
): ReadonlyMap<string, RoleNavigatorQueueCountEntry> {
  return new Map(entries.map((entry) => [entry.countSource, entry]));
}

export function mergeRoleNavigatorQueuesWithCounts(
  queues: readonly RoleNavigatorQueueConfig[],
  counts:
    | ReadonlyMap<string, RoleNavigatorQueueCountEntry>
    | Record<string, RoleNavigatorQueueCountEntry>
): RoleNavigatorQueueDisplay[] {
  const countLookup =
    counts instanceof Map
      ? counts
      : new Map(Object.entries(counts).map(([key, value]) => [key, value]));

  return queues.map((queue) => {
    const countSource = queue.badge?.countSource;
    const countEntry = countSource ? countLookup.get(countSource) : undefined;
    const count = countEntry?.count;
    const countStatus = countEntry?.status;
    const countProvenance = countEntry?.source;
    const countUpdatedAt = countEntry?.updatedAt;
    const badge =
      queue.badge ||
      count !== undefined ||
      countStatus !== undefined ||
      countProvenance !== undefined ||
      countUpdatedAt !== undefined
        ? {
            ...(count !== undefined ? { count } : {}),
            ...(countStatus ? { status: countStatus } : {}),
            ...(countProvenance ? { source: countProvenance } : {}),
            ...(countUpdatedAt ? { updatedAt: countUpdatedAt } : {}),
            ...(queue.badge?.severity ? { severity: queue.badge.severity } : {}),
          }
        : undefined;

    return {
      id: queue.id,
      label: queue.label,
      ...(queue.description ? { description: queue.description } : {}),
      domain: queue.domain,
      filter: queue.filter,
      ...(queue.defaultAction ? { defaultAction: queue.defaultAction } : {}),
      ...(badge ? { badge } : {}),
    };
  });
}

/** Apply loading badge status while counts are in-flight (no fabricated numbers). */
export function markRoleNavigatorQueuesLoadingIfNeeded(
  queues: readonly RoleNavigatorQueueDisplay[],
  isLoading: boolean
): RoleNavigatorQueueDisplay[] {
  if (!isLoading) {
    return [...queues];
  }

  return queues.map((queue) => {
    if (!queue.badge || queue.badge.status === 'error' || queue.badge.status === 'unknown') {
      return queue;
    }
    if (queue.badge.count !== undefined || queue.badge.status === 'ok') {
      return queue;
    }
    return {
      ...queue,
      badge: {
        ...queue.badge,
        status: 'loading',
      },
    };
  });
}

export function formatRoleNavigatorQueueBadgeLabel(badge: RoleNavigatorQueueBadge): {
  primary: string;
  provenance?: string;
} {
  if (badge.status === 'loading') {
    return { primary: '…' };
  }
  if (badge.status === 'error') {
    return { primary: '!' };
  }
  if (badge.status === 'unknown') {
    return { primary: '—' };
  }
  if (badge.status === 'stale') {
    return {
      primary: badge.count !== undefined ? String(badge.count) : '…',
      provenance: 'stale',
    };
  }
  const primary = badge.count !== undefined ? String(badge.count) : '—';
  if (badge.source === 'server' && badge.status === 'ok') {
    return { primary, provenance: 'live' };
  }
  return { primary };
}

export function markRoleNavigatorQueuesStaleIfNeeded(
  queues: readonly RoleNavigatorQueueDisplay[],
  lastFetchedAtMs: number | undefined,
  staleTtlMs: number = ROLE_QUEUE_COUNT_DISPLAY_STALE_TTL_MS
): RoleNavigatorQueueDisplay[] {
  if (!lastFetchedAtMs || Date.now() - lastFetchedAtMs <= staleTtlMs) {
    return [...queues];
  }

  return queues.map((queue) => ({
    ...queue,
    badge: queue.badge
      ? {
          ...queue.badge,
          status:
            queue.badge.status === 'error' || queue.badge.status === 'unknown'
              ? queue.badge.status
              : 'stale',
        }
      : { status: 'stale' },
  }));
}
